import { getEdgeCategoryTypeFromString } from '@modules/common/utils';
import { segregateEntitySentimentPair } from 'components/QueryBuilderView/utils/entitySentimentWidgetHelper';
import {
  FieldName,
  IDField,
  LanguageField,
  sourceDeserializer,
  SourceField,
  SourceNameWithNamespace,
  TaxonomyFieldInstance,
} from 'v2/core/entities/fields';
import {
  EmptyWhereClause,
  MetadataValue,
  OperationType,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

type getWhereClauseForFieldNameParameters = {
  fieldName: FieldName | string | null;
  values: any[];
  op: OperationType;
  not?: boolean;
  qbField?: string;
};

export const getWhereClauseForFieldName = ({
  fieldName,
  values,
  op,
  not,
  qbField,
}: getWhereClauseForFieldNameParameters) => {
  switch (fieldName) {
    case 'Entity':
      return entityWhereClauseGenerator(values, op, not);
    case 'Reason':
      return reasonWhereClauseGenerator(values, op, not);
    case 'Theme':
      return labelHierarchyWhereClauseGenerator(values, op, not);
    case 'EntitySentiment':
      return entitySentimentWhereClauseGenerator(values, op, not);
    case 'ReviewSentiment':
      return reviewSentimentWhereClauseGenerator(values, op, not);
    case 'Language':
      return languageWhereClauseGenerator(values, op, not);
    case 'ID':
      return idWhereClauseGenerator(values, op, not);
    case 'Source':
      return sourceWhereClauseGenerator(
        values as SourceNameWithNamespace[],
        op,
        not,
      );
    case 'Category':
      return categoryWhereClauseGenerator(values, op, not);
    case null:
      return EmptyWhereClause;
    default:
      const valueType = typeof values?.[0];
      if (valueType === 'string') {
        return metadataWhereClauseGenerator(
          {
            type: 'string',
            values: values as string[],
          },
          not,
          qbField,
        );
      }
      if (valueType === 'number') {
        return metadataWhereClauseGenerator(
          {
            type: 'number',
            values: values as number[],
          },
          not,
          qbField,
        );
      }
      if (valueType === 'boolean') {
        return metadataWhereClauseGenerator(
          {
            type: 'boolean',
            values: values as boolean[],
          },
          not,
          qbField,
        );
      }
      throw Error('invalid metadata values type');
  }
};

export const getWhereClauseValues = (
  fieldName: FieldName,
  values: string[],
) => {
  if (fieldName === 'Source') {
    return values.map((value) => sourceDeserializer(value));
  }
  return values;
};

export const getMetadataKey = (id: string) => {
  return id.split('.').slice(1).join('.');
};

export const metadataWhereClauseGenerator = (
  value: MetadataValue,
  not?: boolean,
  key?: string,
): WhereClause => {
  if (!key) {
    return EmptyWhereClause;
  }

  const metadataKey = getMetadataKey(key);

  const metadataWhereClause: WhereClause = {
    exclude: not,
    filter: {
      metadataFilter: {
        key: metadataKey,
        ...value,
      },
    },
  };

  return metadataWhereClause;
};

interface whereClauseGenerator<T> {
  (
    values: T[],
    operator?: OperationType,
    not?: boolean,
    key?: string,
  ): WhereClause;
}
export const sourceWhereClauseGenerator: whereClauseGenerator<
  SourceField['values'][0]
> = (values, _?: OperationType, not?: boolean): WhereClause => {
  return {
    exclude: not,
    filter: {
      sourceFilter: {
        sources: values,
      },
    },
  };
};
export const combineWithOperator = (
  clauses: WhereClause[],
  op?: OperationType,
  not?: boolean,
): WhereClause => {
  const operator = op ?? OperationType.OR;
  return {
    exclude: not ?? false,
    op: {
      type: operator,
      params: [...clauses],
    },
  };
};

export const languageWhereClauseGenerator: whereClauseGenerator<
  LanguageField['values'][0]['id']
> = (values, _?: OperationType, not?: boolean): WhereClause => {
  return {
    exclude: not,
    filter: {
      languageFilter: {
        iso6393s: values,
      },
    },
  };
};

export const idWhereClauseGenerator: whereClauseGenerator<
  IDField['values'][0]
> = (values, _?: OperationType, not?: boolean): WhereClause => {
  return {
    exclude: not,
    filter: {
      idFilter: {
        ids: values,
      },
    },
  };
};

export const labelHierarchyWhereClauseGenerator: whereClauseGenerator<
  TaxonomyFieldInstance['id']
> = (values, _?: OperationType, not?: boolean): WhereClause => {
  return {
    exclude: not,
    filter: {
      labelHierarchyFilter: {
        nodeIDs: values,
      },
    },
  };
};

export const reviewSentimentWhereClauseGenerator: whereClauseGenerator<
  TaxonomyFieldInstance['id']
> = (values, _?: OperationType, not?: boolean): WhereClause => {
  return {
    exclude: not,
    filter: {
      reviewSentimentFilter: {
        sentimentIDs: values,
      },
    },
  };
};

export const entityWhereClauseGenerator: whereClauseGenerator<
  TaxonomyFieldInstance['id']
> = (values, _?: OperationType, not?: boolean, __?: string): WhereClause => {
  return {
    exclude: not,
    filter: {
      entityFilter: {
        entities: values.map((entityID) => ({ entityID })),
      },
    },
  };
};

export const reasonWhereClauseGenerator: whereClauseGenerator<
  TaxonomyFieldInstance['id']
> = (values, _?: OperationType, not?: boolean, __?: string): WhereClause => {
  return {
    exclude: not,
    filter: {
      reasonFilter: {
        reasonIDs: values,
      },
    },
  };
};

export const categoryWhereClauseGenerator: whereClauseGenerator<string> = (
  values,
  _?: OperationType,
  __?: boolean,
): WhereClause => {
  return {
    filter: {
      categoryFilter: {
        categories: values.map((value) =>
          String(getEdgeCategoryTypeFromString(value))?.toLowerCase(),
        ),
      },
    },
  };
};

export const hybridSearchWhereClauseGenerator: whereClauseGenerator<string> = (
  values,
  _?: OperationType,
  not?: boolean,
): WhereClause => {
  return {
    exclude: not,
    filter: {
      hybridSearchFilter: {
        query: '',
        queries: values,
      },
    },
  };
};

export const entitySentimentWhereClauseGenerator: whereClauseGenerator<string> =
  (values, _?: OperationType, not?: boolean): WhereClause => {
    const entities = values.map((value) => {
      const [sentimentValue, entityValue] = segregateEntitySentimentPair(value);
      return {
        sentiment: sentimentValue,
        entityID: entityValue,
      };
    });

    return {
      exclude: not,
      filter: {
        entityFilter: {
          entities,
        },
      },
    };
  };
